// Centralized EN / VI content. English follows the approved
// "Chitta Group Homepage Content Direction_V2" DOCX verbatim.
// Vietnamese is a faithful professional translation; official names,
// "healthspan" and "Healthspan Intelligence" are preserved.

export const content = {
    en: {
        meta: {
            title: "Chitta Group — Accelerating Proactive Healthcare",
            description:
                "Chitta Group advances proactive healthcare through multidisciplinary research into how nutrition, movement and mind–brain–behaviour shape healthspan.",
        },
        a11y: {
            skip: "Skip to content",
            switchLanguage: "Switch language",
            toggleTheme: "Toggle light and dark mode",
            openMenu: "Open menu",
            closeMenu: "Close menu",
            primaryNav: "Primary navigation",
            home: "Chitta Group — back to top",
        },
        nav: [
            { id: "research", label: "research" },
            { id: "institute", label: "institute" },
            { id: "application", label: "application" },
            { id: "leadership", label: "leadership" },
            { id: "collaboration", label: "collaboration" },
            { id: "contact", label: "contact" },
        ],
        hero: {
            kicker: "Chitta Group",
            lead: "Accelerating",
            accent: "Proactive Healthcare",
            line2: "for Healthier Communities.",
            alt: "An athlete running on a treadmill in a futuristic performance lab while a clinician monitors biometric data",
        },
        intro: {
            kicker: "introduction",
            heading: "Science for Proactive Living.",
            bodyStart: "We take on some of the most important questions: ",
            bodyEmphasis: "how do we stay well, and what can science make possible?",
            bodyMiddle: "Chitta Group advances proactive healthcare through multidisciplinary research into how nutrition, movement and mind–brain–behaviour shape healthspan. By integrating science, data and models, we accelerate discovery ",
            bodyClosing: "for greater impact on human health and contribute to healthier communities.",
            cta: "explore our research",
        },
        research: {
            kicker: "research",
            heading: "Our Research",
            items: [
                {
                    name: "Metabolism",
                    desc: "Mapping metabolic health across energy regulation, glucose, lipids and body composition.",
                    img: "/assets/research-metabolism.webp",
                    alt: "A woman resting inside a futuristic metabolic assessment chamber",
                },
                {
                    name: "Stress + Sleep",
                    desc: "Tracing the rhythms that govern stress, sleep and recovery.",
                    img: "/assets/research-stress-sleep.webp",
                    alt: "A calm modern sleep suite with integrated health monitoring technology",
                },
                {
                    name: "Cognition",
                    desc: "Profiling the cognitive functions that support attention, memory and decision-making across life.",
                    img: "/assets/research-cognition.webp",
                    alt: "A glowing blue neural network visualisation representing cognition",
                },
                {
                    name: "Movement",
                    desc: "Measuring the capacities that keep the body strong, mobile and functional.",
                    img: "/assets/research-movement.webp",
                    alt: "An athlete running on a treadmill in a movement analysis lab while a specialist reviews motion data",
                },
                {
                    name: "Recovery + Endurance",
                    desc: "Testing how the body responds, recovers and adapts to physical demand.",
                    img: "/assets/research-recovery.webp",
                    alt: "A clinician guiding a recovery session with compression therapy equipment",
                },
                {
                    name: "Nutrition + Vitality",
                    desc: "Connecting nutrition and digestion with energy, resilience and long-term function.",
                    img: "/assets/research-nutrition.webp",
                    alt: "A modern nutrition analysis system with sample cartridges and a health data display",
                },
            ],
        },
        institute: {
            kicker: "institute",
            name: "Chitta Healthspan Intelligence Institute",
            status: "Coming soon",
            desc: "Home to Chitta Group’s research, data and Healthspan Intelligence.",
            address: "No. 4 Lieu Giai, Ngoc Ha, Hanoi, Vietnam",
            addressLabel: "location",
            img: "/assets/interior-lobby.jpg",
            alt: "Elegant Chitta Group lobby interior with the brand wall",
        },
        application: {
            kicker: "application",
            heading: "From Scientific Insight to Action.",
            body: "We create environments where research moves into application, measurement and continuous improvement.",
            items: [
                {
                    name: "CHITTA HEALTHSPAN CENTER",
                    desc: "Assessment and structured healthspan programmes in an urban setting.",
                    img: "/assets/interior-center.jpg",
                    alt: "Elegant atrium interior of Chitta Healthspan Center",
                },
                {
                    name: "CHITTA RETREAT",
                    desc: "Immersive programmes across movement, nutrition, recovery and mindfulness.",
                    img: "/assets/application-retreat.webp",
                    alt: "A premium wellness training space with a treadmill, coach and views into greenery",
                },
                {
                    name: "CHITTA TECHNOLOGY",
                    desc: "A digital layer connecting health information, measurement and follow-up.",
                    img: "/assets/application-technology.webp",
                    alt: "A person holding a tablet showing a personal health metrics dashboard",
                },
            ],
        },
        leadership: {
            kicker: "leadership",
            heading: "Strategic, Medical and Governance Leadership.",
            cta: "meet our leadership",
            portraitNote: "official portrait to be supplied",
            members: [
                {
                    name: "Tuan Tran Ngoc",
                    title: "Founder & Chairman",
                    line: "More than 23 years of experience in investment and enterprise development.",
                    initials: "TT",
                    img: "/assets/leadership-tuan-tran.jpg",
                    imgAlt: "Official portrait of Tuan Tran Ngoc",
                },
                {
                    name: "Dr Olivia Ly Lesslar",
                    title: "Medical Advisor",
                    line: "Medical doctor working across psychoneuroimmunology, longevity and complex-condition medicine.",
                    initials: "OL",
                },
                {
                    name: "Anthony Jude Tan",
                    title: "Secretary General",
                    line: "More than four decades across preventive health, hospitality and longevity.",
                    initials: "AT",
                    img: "/assets/leadership-anthony-jude-tan.jpg",
                    imgAlt: "Official portrait of Anthony Jude Tan",
                },
                {
                    name: "Nghia Nguyen Chinh",
                    title: "Audit & Governance Advisor",
                    line: "More than 20 years across accounting, auditing, corporate strategy and enterprise governance.",
                    initials: "NC",
                    img: "/assets/leadership-nghia-nguyen.jpg",
                    imgAlt: "Official portrait of Nghia Nguyen Chinh",
                },
            ],
        },
        collaboration: {
            heading: "Advancing proactive healthcare through collaboration.",
            cta: "partner with chitta",
            areas: [
                {
                    name: "RESEARCH & UNIVERSITIES",
                    desc: "Research design, methodology, validation, publication and training.",
                },
                {
                    name: "HEALTHCARE & CLINICAL",
                    desc: "Clinical expertise, safety, referral, co-care and translational research.",
                },
                {
                    name: "TECHNOLOGY & DATA",
                    desc: "Technology assessment, measurement, integration, data quality and responsible AI.",
                },
                {
                    name: "EXPERTS & FACULTY",
                    desc: "Scientific advisory, domain leadership, curriculum, teaching and mentoring.",
                },
            ],
        },
        contact: {
            kicker: "contact",
            heading: "One Direct Route to Engage.",
            formHeading: "Contact Chitta",
            fields: {
                name: "Full name",
                email: "Email",
                organisation: "Organisation",
                message: "Message",
            },
            placeholders: {
                name: "Your full name",
                email: "you@organisation.com",
                organisation: "Your organisation",
                message: "How would you like to engage with Chitta?",
            },
            errors: {
                name: "Please enter your full name.",
                email: "Please enter a valid email address.",
                organisation: "Please enter your organisation.",
                message: "Please enter a message.",
            },
            submit: "send message",
            sending: "sending…",
            success: "Thank you. Your message has been received — Chitta Group will be in touch.",
            error: "Something went wrong. Please try again.",
        },
        footer: {
            address: "No. 4 Lieu Giai, Ngoc Ha, Hanoi, Vietnam",
            navLabel: "sections",
            rights: "All rights reserved.",
        },
    },
    vi: {
        meta: {
            title: "Chitta Group — Thúc đẩy Chăm sóc Sức khỏe Chủ động",
            description:
                "Chitta Group thúc đẩy chăm sóc sức khỏe chủ động thông qua nghiên cứu đa ngành về cách dinh dưỡng, vận động và tâm trí–não bộ–hành vi định hình healthspan.",
        },
        a11y: {
            skip: "Chuyển đến nội dung chính",
            switchLanguage: "Chuyển đổi ngôn ngữ",
            toggleTheme: "Chuyển đổi chế độ sáng và tối",
            openMenu: "Mở menu",
            closeMenu: "Đóng menu",
            primaryNav: "Điều hướng chính",
            home: "Chitta Group — về đầu trang",
        },
        nav: [
            { id: "research", label: "nghiên cứu" },
            { id: "institute", label: "viện" },
            { id: "application", label: "ứng dụng" },
            { id: "leadership", label: "lãnh đạo" },
            { id: "collaboration", label: "hợp tác" },
            { id: "contact", label: "liên hệ" },
        ],
        hero: {
            kicker: "Chitta Group",
            lead: "Thúc Đẩy",
            accent: "Chăm Sóc Sức Khỏe Chủ Động",
            line2: "Vì Những Cộng Đồng Khỏe Mạnh Hơn.",
            alt: "Một vận động viên chạy trên máy trong phòng thí nghiệm hiệu suất tương lai trong khi chuyên viên theo dõi dữ liệu sinh trắc học",
        },
        intro: {
            kicker: "giới thiệu",
            heading: "Khoa Học Cho Lối Sống Chủ Động.",
            bodyStart: "Chúng tôi theo đuổi một số câu hỏi quan trọng nhất: ",
            bodyEmphasis: "làm thế nào để duy trì sức khỏe, và khoa học có thể mở ra điều gì?",
            bodyMiddle: "Chitta Group thúc đẩy chăm sóc sức khỏe chủ động thông qua nghiên cứu đa ngành về cách dinh dưỡng, vận động và tâm trí–não bộ–hành vi định hình healthspan. Bằng cách tích hợp khoa học, dữ liệu và mô hình, chúng tôi đẩy nhanh tốc độ khám phá ",
            bodyClosing: "để tạo ra tác động lớn hơn đến sức khỏe con người và đóng góp vào những cộng đồng khỏe mạnh hơn.",
            cta: "khám phá nghiên cứu của chúng tôi",
        },
        research: {
            kicker: "nghiên cứu",
            heading: "Nghiên Cứu Của Chúng Tôi",
            items: [
                {
                    name: "Trao Đổi Chất",
                    desc: "Lập bản đồ sức khỏe trao đổi chất qua điều hòa năng lượng, glucose, lipid và thành phần cơ thể.",
                    img: "/assets/research-metabolism.webp",
                    alt: "Một phụ nữ nghỉ ngơi trong buồng đánh giá trao đổi chất tương lai",
                },
                {
                    name: "Stress + Giấc Ngủ",
                    desc: "Truy tìm các nhịp điệu điều khiển stress, giấc ngủ và phục hồi.",
                    img: "/assets/research-stress-sleep.webp",
                    alt: "Phòng ngủ hiện đại, thư giãn với công nghệ theo dõi sức khỏe tích hợp",
                },
                {
                    name: "Nhận Thức",
                    desc: "Xây dựng hồ sơ các chức năng nhận thức hỗ trợ sự tập trung, trí nhớ và ra quyết định trong suốt cuộc đời.",
                    img: "/assets/research-cognition.webp",
                    alt: "Hình ảnh mạng lưới thần kinh xanh phát sáng đại diện cho nhận thức",
                },
                {
                    name: "Vận Động",
                    desc: "Đo lường các năng lực giữ cho cơ thể khỏe mạnh, linh hoạt và vận hành tốt.",
                    img: "/assets/research-movement.webp",
                    alt: "Vận động viên chạy trên máy trong phòng phân tích chuyển động trong khi chuyên gia xem dữ liệu vận động",
                },
                {
                    name: "Phục Hồi + Sức Bền",
                    desc: "Kiểm tra cách cơ thể phản ứng, phục hồi và thích nghi với nhu cầu thể chất.",
                    img: "/assets/research-recovery.webp",
                    alt: "Chuyên viên hướng dẫn buổi phục hồi với thiết bị liệu pháp nén",
                },
                {
                    name: "Dinh Dưỡng + Sức Sống",
                    desc: "Kết nối dinh dưỡng và tiêu hóa với năng lượng, sức đề kháng và chức năng lâu dài.",
                    img: "/assets/research-nutrition.webp",
                    alt: "Hệ thống phân tích dinh dưỡng hiện đại với hộp mẫu và màn hình dữ liệu sức khỏe",
                },
            ],
        },
        institute: {
            kicker: "viện",
            name: "Chitta Healthspan Intelligence Institute",
            status: "Sắp ra mắt",
            desc: "Ngôi nhà chung của nghiên cứu, dữ liệu và Healthspan Intelligence của Chitta Group.",
            address: "Số 4 Liễu Giai, Ngọc Hà, Hà Nội, Việt Nam",
            addressLabel: "địa điểm",
            img: "/assets/interior-lobby.jpg",
            alt: "Nội thất sảnh Chitta Group thanh lịch với tường thương hiệu",
        },
        application: {
            kicker: "ứng dụng",
            heading: "Từ Hiểu Biết Khoa Học Đến Hành Động.",
            body: "Chúng tôi kiến tạo các môi trường nơi nghiên cứu chuyển hóa thành ứng dụng, đo lường và cải tiến liên tục.",
            items: [
                {
                    name: "CHITTA HEALTHSPAN CENTER",
                    desc: "Đánh giá và các chương trình healthspan có cấu trúc trong bối cảnh đô thị.",
                    img: "/assets/interior-center.jpg",
                    alt: "Nội thất giếng trời sang trọng của Chitta Healthspan Center",
                },
                {
                    name: "CHITTA RETREAT",
                    desc: "Các chương trình trải nghiệm chuyên sâu về vận động, dinh dưỡng, phục hồi và chánh niệm.",
                    img: "/assets/application-retreat.webp",
                    alt: "Không gian tập luyện wellness cao cấp với máy chạy, huấn luyện viên và tầm nhìn ra thiên nhiên",
                },
                {
                    name: "CHITTA TECHNOLOGY",
                    desc: "Một lớp kỹ thuật số kết nối thông tin sức khỏe, đo lường và theo dõi tiếp nối.",
                    img: "/assets/application-technology.webp",
                    alt: "Một người cầm máy tính bảng hiển thị bảng dữ liệu sức khỏe cá nhân",
                },
            ],
        },
        leadership: {
            kicker: "lãnh đạo",
            heading: "Lãnh Đạo Chiến Lược, Y Khoa và Quản Trị.",
            cta: "gặp gỡ đội ngũ lãnh đạo",
            portraitNote: "chân dung chính thức sẽ được bổ sung",
            members: [
                {
                    name: "Tuan Tran Ngoc",
                    title: "Nhà sáng lập kiêm Chủ tịch",
                    line: "Hơn 23 năm kinh nghiệm trong đầu tư và phát triển doanh nghiệp.",
                    initials: "TT",
                    img: "/assets/leadership-tuan-tran.jpg",
                    imgAlt: "Chân dung chính thức của Tuan Tran Ngoc",
                },
                {
                    name: "Dr Olivia Ly Lesslar",
                    title: "Cố vấn Y khoa",
                    line: "Bác sĩ y khoa với công việc trải rộng tâm–thần kinh–miễn dịch học, tuổi thọ và y học các bệnh lý phức tạp.",
                    initials: "OL",
                },
                {
                    name: "Anthony Jude Tan",
                    title: "Tổng Thư ký",
                    line: "Hơn bốn thập kỷ kinh nghiệm trong y tế dự phòng, khách sạn và tuổi thọ.",
                    initials: "AT",
                    img: "/assets/leadership-anthony-jude-tan.jpg",
                    imgAlt: "Chân dung chính thức của Anthony Jude Tan",
                },
                {
                    name: "Nghia Nguyen Chinh",
                    title: "Cố vấn Kiểm toán & Quản trị",
                    line: "Hơn 20 năm kinh nghiệm trong kế toán, kiểm toán, chiến lược doanh nghiệp và quản trị tổ chức.",
                    initials: "NC",
                    img: "/assets/leadership-nghia-nguyen.jpg",
                    imgAlt: "Chân dung chính thức của Nghia Nguyen Chinh",
                },
            ],
        },
        collaboration: {
            heading: "Thúc đẩy chăm sóc sức khỏe chủ động thông qua hợp tác.",
            cta: "hợp tác cùng chitta",
            areas: [
                {
                    name: "NGHIÊN CỨU & CÁC TRƯỜNG ĐẠI HỌC",
                    desc: "Thiết kế nghiên cứu, phương pháp luận, thẩm định, xuất bản và đào tạo.",
                },
                {
                    name: "Y TẾ & LÂM SÀNG",
                    desc: "Chuyên môn lâm sàng, an toàn, giới thiệu, đồng chăm sóc và nghiên cứu chuyển dịch.",
                },
                {
                    name: "CÔNG NGHỆ & DỮ LIỆU",
                    desc: "Đánh giá công nghệ, đo lường, tích hợp, chất lượng dữ liệu và AI có trách nhiệm.",
                },
                {
                    name: "CHUYÊN GIA & GIẢNG VIÊN",
                    desc: "Tư vấn khoa học, lãnh đạo chuyên môn, chương trình học, giảng dạy và cố vấn.",
                },
            ],
        },
        contact: {
            kicker: "liên hệ",
            heading: "Một Lối Kết Nối Trực Tiếp.",
            formHeading: "Liên Hệ Chitta",
            fields: {
                name: "Họ và tên",
                email: "Email",
                organisation: "Tổ chức",
                message: "Lời nhắn",
            },
            placeholders: {
                name: "Họ và tên của bạn",
                email: "ban@tochuc.com",
                organisation: "Tổ chức của bạn",
                message: "Bạn muốn kết nối với Chitta như thế nào?",
            },
            errors: {
                name: "Vui lòng nhập họ và tên.",
                email: "Vui lòng nhập địa chỉ email hợp lệ.",
                organisation: "Vui lòng nhập tên tổ chức.",
                message: "Vui lòng nhập lời nhắn.",
            },
            submit: "gửi lời nhắn",
            sending: "đang gửi…",
            success: "Cảm ơn bạn. Lời nhắn đã được tiếp nhận — Chitta Group sẽ sớm liên hệ lại.",
            error: "Đã có lỗi xảy ra. Vui lòng thử lại.",
        },
        footer: {
            address: "Số 4 Liễu Giai, Ngọc Hà, Hà Nội, Việt Nam",
            navLabel: "mục",
            rights: "Bảo lưu mọi quyền.",
        },
    },
};
